// React Dependencies
import React, { useEffect, useRef, useState } from 'react';

// External Dependencies
import { Paper } from '@material-ui/core';
import { Pagination, Skeleton } from '@material-ui/lab';
import { Add } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../components/Common/MText';
import { containerHeaderTextStyle } from '../../../styles/common';
import Colours from '../../../styles/colours';
import MButton from '../../../components/MButton';
import ApiService from '../../../services/apiService';
import ProgressiveImage from '../../../components/ProgressiveImage';
import { imageResizeUrl } from '../../../common/convert';
import PhotoDetailsModal from './PhotoDetailsModal';
import { IsEmpty } from '../../../common/checks';
import { Url } from '../../../common/constants';
import ToolTip from '../../../components/Common/ToolTip';

const MAX_PHOTOS_PER_PAGE = 36;

const Snapstyr = (props) => {
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [poiCoord, setPoiCoord] = useState(null);

  const containerRef = useRef(null);

  const onPageChange = (e, page) => {
    setPageNo(page);
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const fetchPhotos = async () => {
    setLoading(true);

    try {
      const result = await ApiService.getContributedPoiPhotos(props.selectedPage.id, {
        pageNo,
        pageSize: MAX_PHOTOS_PER_PAGE,
      });

      setPhotos(result.rows);
      setTotalCount(result.totalCount);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [pageNo]);

  return (
    <div className="container-fluid" ref={containerRef} style={{ minHeight: '100dvh' }}>
      <div className="row d-flex flex-column">
        <div className="col-12 col-xl-11 my-3">
          <Paper className="px-4 pt-2 pb-4 mb-5">
            <div className="ml-1 mt-2 mb-3 d-flex flex-row justify-content-start align-items-center">
              <MText
                size={containerHeaderTextStyle.fontSize}
                bold
                text="Snapstyr"
              />

              <ToolTip title="Add Snapstyr photos">
                <MButton
                  disableElevation
                  style={{
                    border:       '1px solid black',
                    borderRadius: 99,
                  }}
                  textSize={containerHeaderTextStyle.actionBtn.textSize}
                  text="Photos"
                  type="button"
                  className="ml-3 px-4"
                  onClick={() => props.history.push(Url.SnapstyrAdd.replace(':pageId', props.selectedPage.id))}
                  icon={() => <Add style={{ fontSize: 11, marginRight: 5 }} />}
                />
              </ToolTip>
            </div>
            <div
              className="row"
              style={{ rowGap: 24 }}
            >
              { loading ? (
                new Array(MAX_PHOTOS_PER_PAGE).fill(0).map((_, idx) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`skeleton-${idx}`}
                    className="col-4 col-md-3"
                    style={{
                      paddingRight: 12,
                      paddingLeft:  12,
                    }}
                  >
                    <div style={{ width: '100%', aspectRatio: 1 }}>
                      <Skeleton
                        variant="rect"
                        style={{
                          width:  '100%',
                          height: '100%',
                        }}
                      />
                    </div>
                  </div>
                )))
                : (
                  photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="col-4 col-md-3"
                      style={{ paddingRight: 12, paddingLeft: 12 }}
                    >
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions*/}
                      <div
                        style={{
                          width:       '100%',
                          aspectRatio: 1,
                          overflow:    'hidden',
                          cursor:      'pointer',
                        }}
                        onClick={() => {
                          setSelectedPhoto(photo);
                          setPoiCoord(photo.poiLocation);
                        }}
                      >
                        <ProgressiveImage
                          image={imageResizeUrl(photo.display, 300, 300)}
                          width="100%"
                          height="100%"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  ))
                )}
            </div>
          </Paper>
          {
            totalCount > 0 && (
              <div
                className="position-fixed py-2 pr-5 d-flex flex-row align-items-center"
                style={{
                  background:   Colours.White,
                  bottom:       5,
                  width:        '100%',
                  borderRadius: '10px',
                  zIndex:       99,
                }}
              >
                <Pagination
                  color="primary"
                  size="large"
                  count={Math.ceil(totalCount / MAX_PHOTOS_PER_PAGE)}
                  page={pageNo}
                  onChange={onPageChange}
                  className="float-left"
                />
                <MText className="ml-3" text={`Total count: ${totalCount}`} />
              </div>
            )
          }
        </div>
      </div>

      <PhotoDetailsModal
        open={!IsEmpty(selectedPhoto)}
        photo={selectedPhoto}
        poiCoord={poiCoord}
        onClose={() => setSelectedPhoto(null)}
        onMutationComplete={() => {
          setSelectedPhoto(null);
          fetchPhotos();
        }}
      />
    </div>
  );
};

Snapstyr.propTypes = {
  selectedPage: PropTypes.shape({
    id:        PropTypes.number.isRequired,
    plan:      PropTypes.string,
    staffType: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  selectedPage: state.page.selected,
});

export default connect(mapStateToProps, null)(Snapstyr);
